import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Copy } from "lucide-react";

export function MonthlyReport({ clientId }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);

  useEffect(() => {
    setReport(null);
    api.get(`/clinician/client/${clientId}/report`, { params: { month } }).then((r) => setReport(r.data));
  }, [clientId, month]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report.text);
      toast.success("Summary copied — paste it into your notes.");
    } catch (_) {
      const el = document.querySelector('[data-testid="report-text"]');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      toast("Clipboard blocked by the browser — the text is selected, press Ctrl/Cmd+C.");
    }
  };

  return (
    <div className="warm-card p-6" data-testid="monthly-report">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-700" />
          <h3 className="font-display font-bold text-stone-900">Monthly summary</h3>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-1.5 rounded-xl border border-stone-300 text-xs" />
          <button data-testid="report-copy" onClick={copy} disabled={!report} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-amber-700 disabled:opacity-50">
            <Copy className="w-3.5 h-3.5" /> Copy for notes
          </button>
        </div>
      </div>
      {!report ? (
        <div className="text-sm text-stone-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            {[["Check-ins", report.checkins], ["Goals met", report.weeks_met], ["Points", `+${report.points}`], ["Level-ups", report.level_ups.length], ["Shields", report.shields_used]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-stone-50 p-3 text-center">
                <div className="font-mono text-xl font-bold text-amber-800">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{l}</div>
              </div>
            ))}
          </div>
          <pre data-testid="report-text" className="whitespace-pre-wrap text-xs text-stone-700 font-mono bg-stone-50 rounded-2xl p-4 max-h-72 overflow-y-auto leading-relaxed">{report.text}</pre>
        </>
      )}
    </div>
  );
}
